import React, { useState } from 'react';
import { Search, BookOpen, GraduationCap, DollarSign, HelpCircle, CheckCircle, ArrowRight, TrendingUp, PieChart, Target, Brain, Wallet, Globe, X, ChevronRight, Play, ExternalLink, AlertCircle } from 'lucide-react';
import { useCountry } from './CountryContext';

const HelpAndLearn = () => {
  const { selectedCountry } = useCountry();
  const [activeSection, setActiveSection] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItem, setExpandedItem] = useState(null);

  // Sections
  const sections = [
    { id: 'getting-started', label: 'Getting Started', icon: Play },
    { id: 'features', label: 'Feature Guides', icon: BookOpen },
    { id: 'investments', label: 'Investment Education', icon: TrendingUp },
    { id: 'accounts', label: 'Tax & Accounts', icon: DollarSign },
    { id: 'glossary', label: 'Glossary', icon: HelpCircle }
  ];

  const toggleItem = (index) => {
    setExpandedItem(expandedItem === index ? null : index);
  };

  // Getting Started Content
  const gettingStartedSteps = [
    {
      title: "1. Add Your Accounts",
      description: "Start by adding your financial accounts (401k, IRA, ISA, SIPP, etc.)",
      steps: [
        "Navigate to the 'Accounts' tab",
        "Click 'Add Account' button",
        "Select your account type from 16 options",
        "Enter account name and institution",
        "Optionally set regular contribution amounts"
      ]
    },
    {
      title: "2. Track Your Investments",
      description: "Add your investments to see your complete portfolio",
      steps: [
        "Go to the 'Portfolio' tab",
        "Click 'Add Investment'",
        "Choose asset class (Stocks, Bonds, Real Estate, etc.)",
        "Enter investment details (name, amount, account)",
        "View your portfolio visualization instantly"
      ]
    },
    {
      title: "3. Plan Your Retirement (Premium)",
      description: "Set retirement goals and see if you're on track",
      steps: [
        "Visit the 'Retirement' tab",
        "Enter your retirement age and desired income",
        "Set your expected returns and inflation rate",
        "Review drawdown simulations and success rates",
        "Adjust your plan based on the results"
      ]
    },
    {
      title: "4. Analyze with AI (Premium)",
      description: "Get AI-powered insights on your portfolio",
      steps: [
        "Upgrade to Premium if not already subscribed",
        "Navigate to 'AI Analysis' tab",
        "Click 'Generate Analysis' for comprehensive insights",
        "Review diversification score, risk assessment",
        "Follow personalized optimization recommendations"
      ]
    }
  ];

  // Feature Guides
  const featureGuides = [
    {
      title: "Understanding Your Dashboard",
      content: "Your dashboard provides a comprehensive overview of your financial situation. The Total Portfolio Value shows your complete net worth across all accounts. Asset allocation charts display how your investments are distributed across stocks, bonds, real estate, commodities, cryptocurrency, and cash. The accounts summary shows individual account balances, and top holdings highlight your largest investments."
    },
    {
      title: "Net Worth Dashboard (Premium)",
      content: "The Net Worth Dashboard provides a complete 360° view of your financial health. It calculates your net worth by adding all assets (investments, cryptocurrency, cash) and subtracting liabilities (mortgages, loans, credit cards). You'll see visual breakdowns with pie charts and bar graphs, a financial health score (0-100), debt-to-asset ratio, and personalized insights. The color-coded rating system helps you quickly assess your financial situation: Excellent (80-100), Good (60-79), Fair (40-59), Poor (20-39), or Critical (0-19)."
    },
    {
      title: "Financial Goals Tracking (Premium)",
      content: "Set and track progress toward your financial goals with intelligent projections. Choose from 11 goal types including Emergency Fund, Home Purchase, Education, Retirement, and more. Enter your target amount, target date, and monthly contribution. The system calculates required monthly savings, projects future value with compound interest, and shows if you're on track. Goals are automatically prioritized based on urgency and progress. Visual progress bars and status indicators (On Track, At Risk, Urgent, Overdue, Completed) help you stay motivated and make informed decisions."
    },
    {
      title: "Liabilities Management (Premium)",
      content: "Track and manage all your debts in one centralized location. Add mortgages, auto loans, student loans, credit cards, personal loans, and other liabilities. For each liability, track the current balance, original amount, interest rate, and monthly payment. Visual payoff progress bars show how much you've paid off. The dashboard displays total liabilities, monthly payment obligations, and integrates with your Net Worth calculation. Use this feature to create a debt payoff strategy and monitor your progress toward becoming debt-free."
    },
    {
      title: "Managing Multiple Accounts",
      content: "PlanLens supports 16 different account types. Each account can hold multiple investments. Use the 'View By Account' option in the Portfolio tab to see holdings organized by account. This helps you track tax-advantaged accounts (like 401k, IRA, ISA, SIPP) separately from taxable brokerage accounts."
    },
    {
      title: "Portfolio Analysis Explained",
      content: "Portfolio Analysis (Premium) uses Monte Carlo simulations to project your portfolio's future performance. It runs up to 10,000 different market scenarios to show you the range of possible outcomes. Historical backtesting (2014-2024) shows how your current allocation would have performed in past market conditions. The Sharpe ratio measures risk-adjusted returns, while volatility indicates how much your portfolio value fluctuates."
    },
    {
      title: "Reading AI Insights",
      content: "AI Analysis (Premium) provides a diversification score from 0-100, where higher is better. The analysis identifies concentration risks (over-investment in single assets), suggests rebalancing opportunities, and provides personalized recommendations in natural language. Risk assessments include volatility, maximum drawdown, and correlation analysis."
    },
    {
      title: "Retirement Planning Tips",
      content: "The retirement planner (Premium) uses your actual portfolio data. Success rate shows the probability of your money lasting throughout retirement. Try different scenarios: conservative (3% withdrawal), moderate (4%), and aggressive (5%). Consider inflation in your projections - a 2-3% annual inflation rate is typical. The drawdown simulation shows how your portfolio value changes over time."
    },
    {
      title: "Exporting Your Data",
      content: "Both Free and Premium plans can export data. Click the export button in any section to download PDF reports or CSV data files. PDF reports are formatted for printing and include charts. CSV files can be opened in Excel or Google Sheets for further analysis. Exports include portfolio snapshots, transaction history, and analysis reports."
    }
  ];

  // Investment Education
  const investmentEducation = [
    {
      title: "Asset Classes Explained",
      icon: PieChart,
      content: [
        {
          subtitle: "Stocks (Equities)",
          text: "Stocks represent ownership in companies. They offer high growth potential but come with higher risk. Historically, stocks have returned 8-10% annually over long periods. Best for long-term goals (10+ years)."
        },
        {
          subtitle: "Bonds (Fixed Income)",
          text: "Bonds are loans to governments or corporations. They provide steady income with lower risk than stocks. Typical returns: 3-5% annually. Best for income and portfolio stability."
        },
        {
          subtitle: "Real Estate",
          text: "Property investments including REITs (Real Estate Investment Trusts). Provides diversification and inflation protection. Returns vary widely by location and property type."
        },
        {
          subtitle: "Commodities",
          text: "Physical goods like gold, silver, oil, and agricultural products. Used as inflation hedges and portfolio diversifiers. More volatile than stocks and bonds."
        },
        {
          subtitle: "Cryptocurrency",
          text: "Digital assets like Bitcoin and Ethereum. Extremely volatile with high risk/reward potential. Recommended to keep under 5% of total portfolio for most investors."
        },
        {
          subtitle: "Cash & Equivalents",
          text: "Savings accounts, money market funds, and short-term bonds. Very low risk with minimal returns. Essential for emergency funds and short-term goals."
        }
      ]
    },
    {
      title: "Diversification Basics",
      icon: Target,
      content: [
        {
          subtitle: "Why Diversify?",
          text: "Don't put all your eggs in one basket. Diversification reduces risk by spreading investments across different asset classes, sectors, and geographic regions. When one investment declines, others may hold steady or rise."
        },
        {
          subtitle: "Asset Allocation",
          text: "Your mix of stocks, bonds, and other assets. A common rule: subtract your age from 110 to get your stock percentage. Example: Age 30 → 80% stocks, 20% bonds. Adjust based on risk tolerance."
        },
        {
          subtitle: "Rebalancing",
          text: "Periodically selling winners and buying losers to maintain your target allocation. Rebalance annually or when allocations drift 5%+ from targets. This enforces 'buy low, sell high' discipline."
        }
      ]
    },
    {
      title: "Risk vs. Return",
      icon: TrendingUp,
      content: [
        {
          subtitle: "Risk Tolerance",
          text: "Your ability and willingness to accept investment losses. Consider: time horizon (longer = more risk acceptable), financial situation (stable income = more risk acceptable), and emotional comfort with volatility."
        },
        {
          subtitle: "Volatility",
          text: "How much investment values fluctuate. Standard deviation measures volatility. Annual volatility of 15% means returns typically vary ±15% from average. Higher volatility = higher risk."
        },
        {
          subtitle: "Sharpe Ratio",
          text: "Measures risk-adjusted returns. Formula: (Return - Risk-free rate) / Volatility. Higher is better. A Sharpe ratio above 1.0 is considered good, above 2.0 is excellent."
        }
      ]
    }
  ];

  // Tax & Accounts Education (US and UK specific)
  const taxEducationUS = [
    {
      title: "401(k) Plans",
      content: "Employer-sponsored retirement accounts. Contributions are pre-tax (Traditional) or post-tax (Roth). 2024 limit: $23,000 ($30,500 if age 50+). Employer matching is free money - always contribute enough to get full match. Withdrawals before 59½ face 10% penalty plus income tax."
    },
    {
      title: "Traditional vs. Roth IRA",
      content: "Traditional IRA: Contributions may be tax-deductible now, but withdrawals are taxed. Roth IRA: Pay taxes now, withdraw tax-free in retirement. 2024 limit: $7,000 ($8,000 if age 50+). Choose Traditional if you expect lower tax bracket in retirement, Roth if you expect higher bracket."
    },
    {
      title: "Health Savings Account (HSA)",
      content: "Triple tax advantage: tax-deductible contributions, tax-free growth, tax-free withdrawals for medical expenses. 2024 limit: $4,150 individual, $8,300 family. Can invest HSA funds for long-term growth. After 65, can withdraw for any purpose (taxed as income, no penalty)."
    },
    {
      title: "529 Education Plans",
      content: "Tax-advantaged savings for education expenses. Contributions grow tax-free. Withdrawals for qualified education expenses are tax-free. No annual contribution limit, but gift tax considerations apply. Can change beneficiary to another family member."
    },
    {
      title: "Taxable Brokerage Accounts",
      content: "No contribution limits or withdrawal restrictions. Investment gains taxed at capital gains rates (0%, 15%, or 20% depending on income). Long-term gains (held 1+ year) taxed lower than short-term. Use for goals before retirement age or after maxing tax-advantaged accounts."
    }
  ];

  const taxEducationUK = [
    {
      title: "ISA (Individual Savings Account)",
      content: "Tax-free savings and investment wrapper. 2024/25 allowance: £20,000 across all ISAs. Types: Stocks & Shares ISA (investments), Cash ISA (savings), Lifetime ISA (first home/retirement), Junior ISA (children). No tax on interest, dividends, or capital gains within ISAs."
    },
    {
      title: "SIPP (Self-Invested Personal Pension)",
      content: "Personal pension with investment control. Tax relief on contributions at your marginal rate (20%, 40%, or 45%). Annual allowance: £60,000 (2024/25). Can't access until age 55 (rising to 57 in 2028). 25% tax-free lump sum at retirement, remainder taxed as income."
    },
    {
      title: "Workplace Pensions",
      content: "Employer-sponsored pension schemes. Minimum auto-enrollment: 5% employee + 3% employer (8% total). Tax relief on contributions. Defined Contribution: pot-based, investment risk on employee. Defined Benefit: salary-based, more secure but rare in private sector."
    },
    {
      title: "Lifetime ISA",
      content: "For first-time home buyers (under £450k) or retirement (60+). Age 18-39 to open, contribute until 50. 25% government bonus on contributions up to £4,000/year (max £1,000 bonus). Counts toward £20,000 ISA allowance. 25% penalty for non-qualifying withdrawals."
    },
    {
      title: "General Investment Account (GIA)",
      content: "Standard taxable investment account. No contribution limits. Capital Gains Tax allowance: £3,000 (2024/25). Dividend allowance: £500 (2024/25). Losses can offset gains. Use after exhausting ISA and pension allowances."
    }
  ];

  // Glossary
  const glossaryTerms = [
    { term: "Asset Allocation", definition: "The distribution of your investments across different asset classes (stocks, bonds, real estate, etc.). Strategic asset allocation is a key driver of portfolio returns and risk." },
    { term: "Diversification", definition: "Spreading investments across multiple assets to reduce risk. A diversified portfolio is less vulnerable to any single investment's poor performance." },
    { term: "Monte Carlo Simulation", definition: "A statistical technique that runs thousands of scenarios to project portfolio outcomes. Shows range of possible results (best case, average, worst case) rather than a single prediction." },
    { term: "Sharpe Ratio", definition: "Measures risk-adjusted returns. Calculated as (portfolio return - risk-free rate) / portfolio volatility. Higher is better. Above 1.0 is good, above 2.0 is excellent." },
    { term: "Volatility", definition: "The degree of price fluctuation in an investment. Measured by standard deviation. Higher volatility means larger price swings and higher risk." },
    { term: "Maximum Drawdown", definition: "The largest peak-to-trough decline in portfolio value. Measures worst-case scenario loss. Important for understanding downside risk." },
    { term: "Compound Interest", definition: "Earning returns on your returns. Over time, compound growth accelerates wealth building. Einstein reportedly called it the 'eighth wonder of the world.'" },
    { term: "Rebalancing", definition: "Adjusting portfolio back to target allocations by selling winners and buying losers. Maintains desired risk level and enforces disciplined buying/selling." },
    { term: "Tax-Advantaged Account", definition: "Accounts with special tax benefits like 401(k), IRA, ISA, SIPP. May offer tax-deductible contributions, tax-free growth, or tax-free withdrawals." },
    { term: "Expense Ratio", definition: "Annual fee charged by funds, expressed as percentage of assets. Lower is better. Index funds typically have expense ratios under 0.20%." },
    { term: "Dollar-Cost Averaging", definition: "Investing a fixed amount regularly regardless of market conditions. Reduces impact of market timing and volatility." },
    { term: "Required Minimum Distribution (RMD)", definition: "US: Mandatory withdrawals from tax-deferred accounts starting at age 73. Amount based on account balance and life expectancy tables." },
    { term: "Capital Gains Tax", definition: "Tax on investment profits. Long-term (held 1+ year) typically taxed lower than short-term. Rates vary by country and income level." },
    { term: "Dividend", definition: "Company profit distributed to shareholders. Can be reinvested for compound growth or taken as income. May be taxed differently than capital gains." },
    { term: "Correlation", definition: "How two investments move together. Correlation of +1 = move identically, -1 = move oppositely, 0 = move independently. Lower correlation improves diversification." }
  ];

  // Filter glossary based on search
  const filteredGlossary = searchQuery
    ? glossaryTerms.filter(item =>
        item.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.definition.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : glossaryTerms;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-8">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Help & Learn</h1>
              <p className="text-gray-600 dark:text-gray-400">Master PlanLens and grow your financial knowledge</p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search help articles, guides, or terms..."
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sticky top-8">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Sections</h3>
              <nav className="space-y-2">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors text-left ${
                        activeSection === section.id
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{section.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Getting Started */}
            {activeSection === 'getting-started' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <Play className="w-8 h-8 text-blue-600" />
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Getting Started with PlanLens</h2>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-8">
                    Follow these simple steps to set up your financial tracking and planning system.
                  </p>

                  <div className="space-y-6">
                    {gettingStartedSteps.map((step, index) => (
                      <div key={index} className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-6">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{step.title}</h3>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">{step.description}</p>
                        <ul className="space-y-2">
                          {step.steps.map((item, idx) => (
                            <li key={idx} className="flex items-start space-x-3">
                              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700 dark:text-gray-300">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Feature Guides */}
            {activeSection === 'features' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <BookOpen className="w-8 h-8 text-blue-600" />
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Feature Guides</h2>
                  </div>

                  <div className="space-y-4">
                    {featureGuides.map((guide, index) => (
                      <div key={index} className="border-2 border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden">
                        <button
                          onClick={() => toggleItem(index)}
                          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <span className="font-semibold text-gray-900 dark:text-white">{guide.title}</span>
                          <ChevronRight className={`w-5 h-5 text-blue-600 transition-transform ${expandedItem === index ? 'rotate-90' : ''}`} />
                        </button>
                        {expandedItem === index && (
                          <div className="px-6 pb-4">
                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{guide.content}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Investment Education */}
            {activeSection === 'investments' && (
              <div className="space-y-6">
                {investmentEducation.map((topic, topicIndex) => {
                  const Icon = topic.icon;
                  return (
                    <div key={topicIndex} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
                      <div className="flex items-center space-x-3 mb-6">
                        <Icon className="w-8 h-8 text-blue-600" />
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{topic.title}</h2>
                      </div>
                      <div className="space-y-6">
                        {topic.content.map((item, idx) => (
                          <div key={idx}>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{item.subtitle}</h3>
                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{item.text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Tax & Accounts */}
            {activeSection === 'accounts' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <DollarSign className="w-8 h-8 text-blue-600" />
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Tax-Advantaged Accounts</h2>
                      <p className="text-gray-600 dark:text-gray-400">Country-specific account types and tax benefits</p>
                    </div>
                  </div>

                  {/* Country Tabs */}
                  <div className="flex space-x-4 mb-8">
                    <div className={`px-6 py-3 rounded-lg font-semibold ${selectedCountry === 'US' ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>
                      🇺🇸 United States
                    </div>
                    <div className={`px-6 py-3 rounded-lg font-semibold ${selectedCountry === 'UK' ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>
                      🇬🇧 United Kingdom
                    </div>
                  </div>

                  <div className="space-y-4">
                    {(selectedCountry === 'US' ? taxEducationUS : taxEducationUK).map((account, index) => (
                      <div key={index} className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-6">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">{account.title}</h3>
                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{account.content}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-700 rounded-xl p-6">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-amber-900 dark:text-amber-200 mb-2">Important Disclaimer</p>
                        <p className="text-sm text-amber-800 dark:text-amber-300">
                          This information is educational only and not financial or tax advice. Tax laws change frequently. Consult a qualified tax professional or financial advisor for personalized guidance.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Glossary */}
            {activeSection === 'glossary' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <HelpCircle className="w-8 h-8 text-blue-600" />
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Financial Glossary</h2>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Understanding financial terms and concepts
                  </p>

                  <div className="space-y-4">
                    {filteredGlossary.map((item, index) => (
                      <div key={index} className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-6">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{item.term}</h3>
                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{item.definition}</p>
                      </div>
                    ))}
                  </div>

                  {filteredGlossary.length === 0 && (
                    <div className="text-center py-12">
                      <HelpCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">No terms found matching "{searchQuery}"</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpAndLearn;
